import React from 'react';
import { Box, Button, Chip, Container, MenuItem, Stack, TextField, Typography } from '@mui/material';

// News/Lecture 페이지와 맞춘 기본 색상입니다.
const colors = {
    text: '#1a1a1a',
    primary: '#005a8d',
    primaryDark: '#004870',
    primaryLight: '#f5f9fc',
    border: '#e7edf2',
    muted: '#555555',
};

const fontFamily = '"Pretendard", "Noto Sans KR", sans-serif';

// 폼 렌더링과 제출 payload에 함께 쓰는 필드 정의입니다.
const fields = [
    {
        key: 'presenting',
        label: '주 호소 / 의뢰 사유',
        required: true,
        lens: '공통',
        helper: '내담자가 무엇 때문에 왔는지, 어떻게 의뢰되었는지',
    },
    {
        key: 'history',
        label: '배경·발달·관계사(간략)',
        lens: 'SCORS / PBT',
        helper: '가족·성장·주요 관계사 중 관련된 부분만',
    },
    {
        key: 'relationship',
        label: '주요 관계 상호작용 패턴(구체 일화 1~2개)',
        lens: 'SCORS 핵심',
        helper: '실제 있었던 상호작용 장면을 구체적으로',
    },
    {
        key: 'affect',
        label: '정서 경험과 조절 양상',
        lens: 'SCORS / PBT',
        helper: '어떤 정서를 어떻게 겪고 다루는가',
    },
    {
        key: 'function',
        label: '증상·어려움이 맞물리는 상황·기능',
        lens: 'PBT/EEMM 핵심',
        helper: '언제·어떤 맥락에서 악화/유지되는가',
    },
    {
        key: 'changetalk',
        label: '변화에 대한 내담자의 언어(실제 표현)',
        lens: 'MI 핵심',
        helper: "'하고 싶다/못 하겠다/양가적' 등 실제 말",
    },
    {
        key: 'risk',
        label: '위험 관련 신호',
        required: true,
        lens: 'Stage 0 플래그',
        helper: "자·타해, 학대·방임, 급성 위기 언급 유무. 없으면 '없음'이라고 적으세요",
    },
];

const models = ['모델 1', '모델 2', '모델 3'];

function CaseConceptualization() {
    const handleSubmit = (event) => {
        event.preventDefault();
        // API 연결 전까지 입력값 구조를 콘솔에서 확인합니다.
        const data = Object.fromEntries(new FormData(event.currentTarget));
        console.log('case conceptualization form', data);
    };

    return (
        <Box sx={{ bgcolor: '#ffffff', color: colors.text, minHeight: '100vh', fontFamily }}>
            <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#ffffff' }}>
                <Container maxWidth="lg">
                    {/* 페이지 소개 */}
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', md: 'flex-end' }}
                        spacing={2}
                        sx={{ mb: { xs: 4, md: 6 } }}
                    >
                        <Box>
                            <Typography
                                variant="h4"
                                sx={{ fontWeight: 800, mb: 1.5, letterSpacing: 0 }}
                            >
                                AI 사례개념화 가이드
                            </Typography>
                            <Typography sx={{ color: colors.muted, wordBreak: 'keep-all' }}>
                                초보 임상가를 위한 사례개념화 가이드입니다. 사례 정보를 입력하면
                                AI가 개념화 방향을 정리해 줍니다.
                            </Typography>
                        </Box>
                        <Chip
                            label="Free (로그인 필요)"
                            sx={{
                                bgcolor: colors.primaryLight,
                                color: colors.primaryDark,
                                fontWeight: 800,
                                fontSize: { xs: 12, md: 14 },
                                px: 1,
                            }}
                        />
                    </Stack>

                    {/* 사례개념화 입력 폼 */}
                    <Box
                        component="form"
                        onSubmit={handleSubmit}
                        sx={{
                            border: `1px solid ${colors.border}`,
                            borderRadius: 4,
                            bgcolor: '#ffffff',
                            boxShadow: '0 10px 24px rgba(15, 23, 42, 0.04)',
                            p: { xs: 2.5, md: 4 },
                        }}
                    >
                        <Stack spacing={3}>
                            {fields.map((field) => (
                                <Box key={field.key}>
                                    {/* 필드명과 매핑 렌즈 */}
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        justifyContent="space-between"
                                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                                        spacing={1}
                                        sx={{ mb: 1 }}
                                    >
                                        <Typography sx={{ fontWeight: 800, wordBreak: 'keep-all' }}>
                                            {field.label}
                                            {field.required && (
                                                <Box component="span" sx={{ color: '#d32f2f', ml: 0.5 }}>
                                                    *
                                                </Box>
                                            )}
                                        </Typography>
                                        <Chip
                                            label={field.lens}
                                            size="small"
                                            sx={{
                                                bgcolor: colors.primaryLight,
                                                color: colors.primaryDark,
                                                fontWeight: 700,
                                            }}
                                        />
                                    </Stack>
                                    <TextField
                                        name={field.key}
                                        required={field.required}
                                        multiline
                                        minRows={4}
                                        fullWidth
                                        placeholder={field.helper}
                                        helperText={field.helper}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 3,
                                                bgcolor: '#fbfcfe',
                                            },
                                            '& .MuiInputBase-input, & .MuiFormHelperText-root': {
                                                fontFamily,
                                            },
                                        }}
                                    />
                                </Box>
                            ))}

                            {/* 모델 선택과 전송 */}
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                justifyContent="flex-end"
                                spacing={1.5}
                            >
                                <TextField
                                    select
                                    name="model"
                                    label="모델 선택"
                                    defaultValue={models[0]}
                                    size="small"
                                    sx={{
                                        minWidth: { xs: '100%', sm: 160 },
                                        '& .MuiOutlinedInput-root': { borderRadius: 2 },
                                        '& .MuiInputBase-input, & .MuiInputLabel-root': { fontFamily },
                                    }}
                                >
                                    {models.map((model) => (
                                        <MenuItem key={model} value={model} sx={{ fontFamily }}>
                                            {model}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    sx={{
                                        minWidth: 160,
                                        borderRadius: 2,
                                        bgcolor: colors.primary,
                                        fontWeight: 800,
                                        '&:hover': { bgcolor: colors.primaryDark },
                                    }}
                                >
                                    전송
                                </Button>
                            </Stack>
                        </Stack>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
}

export default CaseConceptualization;

